/**
 * As regras locais da Tarefa: as recusas do formulário, as do desfecho, e as derivações que as
 * **três** superfícies compartilham (4.2 + 4.3 + 4.4 + 4.5 + 4.6).
 *
 * **Ele nasceu na 4.6, por extração, e sem uma linha de comportamento novo.** Até a 4.5 tudo isto
 * morava em `TarefasDoNegocio.tsx`, porque havia **um** lugar que criava e concluía Tarefa. Depois
 * da 4.6 há três — o cartão da ficha, a tela de Tarefas e a fila do dia —, e deixá-las lá custava
 * duas coisas: um **ciclo de import** (`ConclusaoDeTarefa` importava as puras do cartão, e o cartão
 * importava o componente de volta), que só era seguro por ordem de avaliação; e o **peso** —
 * `/tarefas` e `/hoje` passariam a arrastar o módulo inteiro do cartão da ficha, e o
 * `tarefas-do-negocio.css` junto, para dentro do bundle delas.
 *
 * **O que mora aqui é o que não desenha nada.** As funções são puras e as constantes são texto de
 * recusa: nenhuma delas lê estado de React, chama rede ou monta JSX. É o mesmo critério que
 * `apresentacao.ts` aplica um degrau acima — lá ficam os textos que **todas** as telas de Negócio
 * escrevem, aqui as regras que as três superfícies **de Tarefa** aplicam.
 *
 * **As puras são de módulo, e não métodos dentro dos componentes, pela razão de sempre:** os ramos
 * que mais importam são os mais difíceis de encenar pela tela. `FALTA_RESPONSAVEL` exige uma sessão
 * sem `usuario_id`; `DESFECHO_LONGO` exige um texto acima do `maxLength` do controle;
 * `rotuloDoResponsavel` responde por um `id` que saiu da equipe entre dois renders. Sem casos que as
 * chamem direto, apagar qualquer um desses ramos deixaria a suíte verde e o envio sairia errado.
 *
 * **As constantes de recusa são exportadas porque a suíte as afirma pelo nome**, e não pelo texto
 * redigitado: uma mensagem reescrita aqui e copiada lá provaria que o teste concorda consigo mesmo.
 */

import {
  DATA,
  FALTA_PRAZO,
  TAMANHO_DO_RESULTADO,
  TAREFA_CONCLUIDA_EM,
  TAREFA_SEM_EMPRESA,
} from './apresentacao'
import type { ConclusaoDaTarefa } from './api'

/**
 * As recusas locais que são **deste** formulário.
 *
 * `FALTA_PRAZO` é a terceira da escada, e ela mora em `apresentacao.ts`: o prazo chegou junto de
 * dois outros textos (`PRAZO_VENCIDO` e o formatador de data pura) que já teriam de morar lá de
 * qualquer forma, e separá-lo deles espalharia o vocabulário do prazo por dois arquivos.
 */
export const FALTA_TITULO = 'Informe o título da tarefa.'
export const FALTA_RESPONSAVEL = 'Escolha o Responsável pela tarefa.'

/**
 * O catálogo chegou **vazio**: não há Tipo nenhum para escolher.
 *
 * Estado real, e não defensivo: o catálogo nasce semeado com os seis do FR-15, mas a exclusão é
 * lógica e o Admin pode excluir todos. Sem esta recusa, o envio sairia com `tipo_de_tarefa_id`
 * vazio e voltaria como um `422` genérico, que não diz o que fazer — e o que há para fazer é ir à
 * Administração, que é outra tela.
 */
export const NAO_HA_TIPO =
  'Não há tipo de tarefa cadastrado. A gestão cria os tipos na Configuração.'

/**
 * Enquanto o catálogo viaja. O seletor fica vazio, e a frase diz por quê — sem ela, "não há tipo
 * cadastrado" apareceria durante o carregamento e mandaria a pessoa à Administração à toa.
 *
 * Ela é exportada pela mesma razão das recusas: `recusaLocal` a devolve, e a suíte a afirma pelo
 * nome em vez de redigitar o texto.
 */
export const CARREGANDO_OS_TIPOS = 'Carregando os tipos…'

/** Falha ao ler o catálogo. Texto do SPA: é falha de carga, não mensagem de domínio. */
export const TIPOS_INDISPONIVEIS =
  'Não foi possível carregar os tipos de tarefa. Recarregue a página antes de seguir — sem eles ' +
  'não há como escolher o tipo.'

/**
 * O que a linha da lista mostra no lugar do nome enquanto a equipe não chegou, ou quando ela
 * falhou, ou quando o uuid não está nela de jeito nenhum.
 *
 * **O usuário desativado não cai aqui**, e a distinção é o FR-65: o nome de quem foi desligado
 * continua aparecendo onde ele já está gravado — o que ele não pode é receber trabalho novo. Por
 * isso a resolução do nome lê `equipe.data` **inteira** (a rota devolve os desativados de
 * propósito) e só as `<option>` do seletor filtram por `ativo`. É o mesmo desenho de
 * `nomeDaPessoa` em `FichaDoNegocio.tsx`, que monta o mapa sem filtro nenhum.
 *
 * Um uuid cru na tela não diz nada a ninguém, e é pior que a frase: ele **parece** informação.
 */
export const RESPONSAVEL_A_CAMINHO = 'responsável a caminho…'

/**
 * O estado da leitura do catálogo de Tipos, reduzido às **três** coisas que a recusa precisa saber.
 *
 * Ele existe porque "não há tipo escolhido" tem três causas, e elas não dizem a mesma coisa: o
 * catálogo ainda não chegou, ele falhou, ou ele chegou vazio. Só a terceira é `NAO_HA_TIPO` — as
 * outras duas mandariam a pessoa à Administração resolver uma coisa que não é problema dela.
 */
export type EstadoDoCatalogo = 'carregando' | 'indisponivel' | 'pronto'

/** O que `recusaLocal` precisa ver — e nada além disso. */
export interface RascunhoDaTarefa {
  titulo: string
  /** O prazo como o `<input type="date">` o entrega: `'2026-09-10'`, ou `''` quando vazio. */
  prazo: string
  /** O Tipo **derivado**: o escolhido, ou o primeiro do catálogo, ou `''` quando não há nenhum. */
  tipoEscolhido: string
  /** O Responsável **derivado**: o escolhido, ou quem entrou, ou `''` sem sessão. */
  responsavelEscolhido: string
  catalogo: EstadoDoCatalogo
}

/**
 * A recusa local, ou `null` quando não há nenhuma. O servidor continua sendo quem decide.
 *
 * **Ela é função pura de módulo, e não um método dentro do componente**, pelo mesmo motivo da irmã
 * de `FormularioDeNegocio.tsx`: um dos ramos não tem como ser alcançado pela tela.
 * `FALTA_RESPONSAVEL` exige uma sessão sem `usuario_id`, e a guarda de rota manda quem não tem
 * sessão para `/entrar` antes de a ficha existir — sem um caso que a chame direto, apagar o ramo
 * deixaria a suíte verde e o envio sairia com `responsavel_id: ""`.
 *
 * A ordem é a que a pessoa preenche — título, prazo, Tipo, Responsável —, e a do Tipo carrega as
 * três causas: a recusa aponta o primeiro campo que precisa mudar, e não o último que reprovou.
 */
export function recusaLocal(rascunho: RascunhoDaTarefa): string | null {
  if (rascunho.titulo.trim() === '') {
    return FALTA_TITULO
  }
  //: O prazo é **obrigatório** no serviço (FR-18), e o `<input type="date">` vazio manda `''` — que
  //: voltaria como o `422` genérico do envelope, sem dizer qual campo faltou. Ele vem logo depois
  //: do título porque é essa a ordem em que o formulário é preenchido.
  //:
  //: Não há nada a validar além do vazio: um `<input type="date">` não entrega data malformada, e
  //: um prazo no **passado** é legítimo — anotar hoje a ligação que devia ter saído ontem faz a
  //: tarefa nascer vencida, que é exatamente o que o vermelho existe para mostrar.
  if (rascunho.prazo === '') {
    return FALTA_PRAZO
  }
  if (rascunho.tipoEscolhido === '') {
    //: As três causas, separadas — e a ordem é a das certezas: a falha primeiro, porque um
    //: catálogo recusado não é resposta final; a ausência de verdade só depois de a leitura ter
    //: terminado. É o mesmo desenho da guarda `catalogoIndisponivel` de `FormularioDeNegocio.tsx`,
    //: que também vem antes de a recusa poder afirmar "este Funil não tem Etapas".
    if (rascunho.catalogo === 'indisponivel') {
      return TIPOS_INDISPONIVEIS
    }
    if (rascunho.catalogo === 'carregando') {
      return CARREGANDO_OS_TIPOS
    }
    return NAO_HA_TIPO
  }
  if (rascunho.responsavelEscolhido === '') {
    return FALTA_RESPONSAVEL
  }
  return null
}

/**
 * O Tipo que o seletor carrega: o escolhido, **enquanto ele existir**, e o primeiro do catálogo em
 * qualquer outro caso.
 *
 * A segunda metade da guarda é o que faz a tela e o envio dizerem a mesma coisa. O Admin pode
 * excluir um Tipo (FR-69) enquanto este formulário está aberto, e a consulta do catálogo o refaz
 * sozinha: sem ela o `<select>` cairia calado na primeira `<option>` — nenhuma casa com o valor
 * gravado — e o envio sairia com o `id` do Tipo excluído, que o serviço recusa com `404` enquanto
 * a tela exibe outro nome.
 *
 * **Ela é função pura de módulo, como `recusaLocal` e `rotuloDoResponsavel`**, e pela mesma razão:
 * o ramo que importa nasce de uma releitura do catálogo entre dois renders, que nenhuma encenação
 * de tela produz de forma determinística.
 *
 * O Responsável resolve o mesmo problema pelo caminho oposto — uma `<option>` sintética que
 * preserva a escolha —, e a diferença é o que cada um pode prometer: um Responsável fora da lista
 * ainda é uma escolha válida no serviço, e um Tipo excluído já não é.
 */
export function tipoDerivado(escolhido: string, tipos: readonly { id: string }[]): string {
  return escolhido !== '' && tipos.some((umTipo) => umTipo.id === escolhido)
    ? escolhido
    : (tipos[0]?.id ?? '')
}

/** O que `rotuloDoResponsavel` precisa ver — e nada além disso. */
export interface QuemResolveOResponsavel {
  /** Quem entrou, quando a sessão já chegou. */
  sessao?: { usuario_id: string; nome: string } | undefined
  /** A equipe **inteira**, com os desativados (FR-65) — nunca a lista filtrada do seletor. */
  daEquipe: readonly { id: string; nome: string }[]
}

/**
 * O nome de quem responde pela tarefa — nunca o uuid cru.
 *
 * A sessão vem primeiro porque ela é a única fonte que não depende de uma ida à rede: com
 * `GET /usuarios/responsaveis` recusado, a própria tarefa da pessoa ainda se identifica. Depois
 * dela vem a equipe **inteira**, com os desativados (FR-65) — o requisito manda o nome do
 * desligado continuar aparecendo onde ele já está gravado.
 *
 * **Ela é função pura de módulo pela mesma razão de `recusaLocal`**: o ramo que mais importa é o
 * mais difícil de encenar pela tela. Ele responde por um `id` que não está entre as `<option>` —
 * um colega escolhido e desativado logo depois, que só uma releitura da equipe produz —, e é
 * justamente o caso em que um rótulo errado faz o seletor exibir **um** nome carregando o `id` de
 * **outro**. Sem um caso que a chame direto, trocá-la de volta por `sessao.nome` deixaria a suíte
 * verde e a tarefa nasceria no nome de quem a tela não mostrou.
 */
export function rotuloDoResponsavel(id: string, quem: QuemResolveOResponsavel): string {
  if (id === quem.sessao?.usuario_id) {
    return quem.sessao.nome
  }
  return quem.daEquipe.find((pessoa) => pessoa.id === id)?.nome ?? RESPONSAVEL_A_CAMINHO
}

/* --- A conclusão com o Resultado opcional (4.4, FR-17) ----------------------------- */

/**
 * O valor que o chip "Outros" carrega no estado da escolha.
 *
 * Ele **não** é o `id` de um Resultado: "Outros" não é linha do catálogo (FR-17), e a semente do
 * FR-15 não a tem. É por isso que ele é uma constante e não uma string solta — o dia em que alguém
 * comparar a escolha com um `id` de verdade, o compilador não ajuda, e este nome é o que deixa a
 * intenção legível nos dois lugares que a leem.
 *
 * O prefixo `#` o torna impossível de colidir com um uuid, que é a outra metade da garantia.
 */
export const RESULTADO_LIVRE = '#outros'

/**
 * A recusa local do desfecho em branco. Exportada pelo nome, como as três do formulário acima: um
 * literal redigitado na suíte provaria que o teste concorda consigo mesmo.
 *
 * Ela é a **mesma frase** que o serviço responde no `400` (`MENSAGEM_DE_RESULTADO_LIVRE_VAZIO`),
 * caractere a caractere — "Resultado" com maiúscula inclusive, que é como o domínio inteiro escreve
 * a entidade. A repetição é deliberada: aqui ela evita a ida à rede, e lá ela alcança quem chamar a
 * rota por fora. Divergir seria pior que não repetir: a mesma recusa passaria a ter duas grafias, e
 * quem batesse nas duas (o texto em branco pela tela, e depois por um cliente qualquer) leria duas
 * mensagens diferentes para o mesmo erro.
 */
export const FALTA_O_DESFECHO = 'Descreva o desfecho, ou conclua sem Resultado.'

/**
 * A recusa local do desfecho longo demais.
 *
 * O `maxLength` do `<textarea>` já impede a digitação, e por isso este ramo não é alcançável pela
 * tela — ele existe pelo mesmo motivo de `MENSAGEM_DE_TITULO_LONGO` no serviço: a guarda de
 * verdade não pode morar só no controle, e quem chamar a pura direto (colando um texto por script,
 * ou uma quarta superfície que monte o modal sem o `maxLength`) receberia o `422` do contrato sem
 * saber o que corrigir.
 */
export const DESFECHO_LONGO = `O desfecho tem no máximo ${String(TAMANHO_DO_RESULTADO)} caracteres.`

/**
 * Por que o título da linha de Tarefa é o que é — as **cinco** causas, em ordem de certeza.
 *
 * `solta` é a única que não depende de rede nenhuma: a Tarefa sem vínculo (FR-20) não tem Empresa
 * a resolver. As outras quatro são as mesmas de qualquer linha que resolve razão social por lote.
 */
export type OrigemDoTituloDaTarefa =
  | 'solta'
  | 'resolvido'
  | 'pendente'
  | 'indisponivel'
  | 'ausente'

/** O lote de nomes de Empresa como as telas o recebem — só o que a escada abaixo lê. */
export interface LoteDeNomesDeEmpresas {
  nomes: ReadonlyMap<string, string>
  carregando: boolean
  falhou: boolean
}

/**
 * O título da linha de Tarefa, e **por que** ele é o que é.
 *
 * Ela mora aqui, e não em cada tela, porque são duas as listas de Tarefa que a escrevem — a tela
 * de Tarefas e a fila do dia — e a escada tem cinco degraus com uma ordem que importa. Duas
 * cópias divergiriam no degrau mais fácil de errar, e as duas telas passariam a explicar a mesma
 * ausência de dois jeitos.
 *
 * **Colapsar as três últimas em "sem empresa" seria afirmar um vínculo falso.** Com o
 * `GET /empresas?ids=` no ar, ou recusado, ou com o nome fora do lote (FR-30), a Tarefa **tem**
 * Empresa — o que falta é o nome dela. Escrever "sem empresa" ali diria à pessoa que aquela Tarefa
 * não está presa a cliente nenhum, exatamente no momento em que a tela não sabe. O que sobra
 * nesses três casos é o **título**, que a linha já escreve logo abaixo, e o aviso acima da lista
 * é quem explica.
 */
export function tituloDaTarefa(
  empresaDoVinculoId: string | null,
  lote: LoteDeNomesDeEmpresas,
): { titulo: string; origemDoTitulo: OrigemDoTituloDaTarefa } {
  if (empresaDoVinculoId === null) {
    return { titulo: TAREFA_SEM_EMPRESA, origemDoTitulo: 'solta' }
  }
  const nome = lote.nomes.get(empresaDoVinculoId)
  if (nome !== undefined) {
    return { titulo: nome, origemDoTitulo: 'resolvido' }
  }
  //: A ordem das guardas é a das certezas: `carregando` antes de `falhou`, porque um lote ainda no
  //: ar pode trazer o nome.
  if (lote.carregando) {
    return { titulo: '', origemDoTitulo: 'pendente' }
  }
  return { titulo: '', origemDoTitulo: lote.falhou ? 'indisponivel' : 'ausente' }
}

/**
 * Falha ao ler a equipe. O envio **continua saindo**: o Responsável cai em quem entrou, que a
 * sessão já traz, e a `<option>` sintética o mostra pelo nome. Por isso o aviso não manda parar —
 * ele diz em nome de quem a tarefa vai nascer se ninguém recarregar.
 *
 * Ela mora aqui desde a 4.6 porque são **duas** as superfícies que criam Tarefa com seletor de
 * Responsável — o formulário do cartão da ficha e o diálogo do aceite da sugestão —, e uma cópia
 * por superfície seria uma delas ficando calada no dia em que a outra mudasse de texto.
 */
export const EQUIPE_INDISPONIVEL =
  'Não foi possível carregar a equipe. O Responsável está em quem entrou: recarregue a página ' +
  'antes de enviar se a tarefa for de outra pessoa.'

/** O que `recusaDoDesfecho` precisa ver — e nada além disso. */
export interface RascunhoDoDesfecho {
  /** O chip escolhido: o `id` de um Resultado, `RESULTADO_LIVRE`, ou `null` quando não há escolha. */
  escolha: string | null
  /** O texto do "Outros", como o `<textarea>` o entrega. */
  texto: string
}

/**
 * A recusa local da conclusão, ou `null` quando não há nenhuma. O servidor continua decidindo.
 *
 * **Ela só tem o que dizer sobre o "Outros"**, e essa é a forma inteira da regra: concluir sem
 * Resultado é `200` (FR-17), e um chip do catálogo é sempre uma escolha válida — o que pode estar
 * errado é o texto livre, que é o único dado que a pessoa digita neste modal.
 *
 * **Ela é função pura de módulo, e não um método dentro do componente**, pelo mesmo motivo de
 * `recusaLocal`: um dos ramos não tem como ser alcançado pela tela. `DESFECHO_LONGO` exige um texto
 * acima do `maxLength` do controle, e sem um caso que a chame direto, apagar o ramo deixaria a
 * suíte verde e o envio sairia para colher um `422` que o cliente já sabia produzir.
 */
export function recusaDoDesfecho(rascunho: RascunhoDoDesfecho): string | null {
  if (rascunho.escolha !== RESULTADO_LIVRE) {
    return null
  }
  const aparado = rascunho.texto.trim()
  if (aparado === '') {
    return FALTA_O_DESFECHO
  }
  if (aparado.length > TAMANHO_DO_RESULTADO) {
    return DESFECHO_LONGO
  }
  return null
}

/**
 * O corpo que a conclusão envia, a partir da escolha do modal — as **três** formas do FR-17.
 *
 * Sem escolha, o corpo é `{}`: concluir sem Resultado. Com um chip do catálogo, vai o `id` — e o
 * serviço congela o rótulo a partir dele, e não a partir de nada que a tela mande. Com "Outros",
 * vai o texto **aparado**, que é o mesmo aparo que o serviço faz do outro lado: " Sem sucesso " e
 * "Sem sucesso" seriam dois Resultados diferentes no relatório do Épico 7.
 *
 * Ela é pura e de módulo pela razão de sempre: é aqui que os três ramos ficam legíveis lado a lado,
 * e é aqui que a suíte os percorre sem encenar três cliques.
 */
export function corpoDaConclusao(rascunho: RascunhoDoDesfecho): ConclusaoDaTarefa {
  if (rascunho.escolha === null) {
    return {}
  }
  if (rascunho.escolha === RESULTADO_LIVRE) {
    return { resultado_livre: rascunho.texto.trim() }
  }
  return { resultado_de_tarefa_id: rascunho.escolha }
}

/**
 * O título do modal: `Concluir {tipo em minúsculas}` (protótipo `:997`).
 *
 * O Tipo entra em minúsculas porque ele é uma **palavra dentro de uma frase** ("Concluir ligação"),
 * e não um rótulo: `tipo_nome` chega capitalizado do catálogo ("Ligação"), e "Concluir Ligação"
 * leria como se "Ligação" fosse o nome próprio de alguma coisa. `toLocaleLowerCase('pt-BR')`, e não
 * `toLowerCase()`, pela mesma razão que o resto do SPA escreve o locale: a regra de caixa é do
 * idioma, e não do ambiente em que o navegador estiver rodando.
 */
export function tituloDaConclusao(tipoNome: string): string {
  return `Concluir ${tipoNome.toLocaleLowerCase('pt-BR')}`
}

/**
 * A marca que substitui o prazo na linha da Tarefa concluída.
 *
 * `concluida_em` é um **instante**, e por isso ela passa por `DATA` — e não por
 * `dataPuraPorExtenso`, que é do `prazo`. Trocar os dois é o engano que a 4.3 documentou dos dois
 * lados, e aqui ele sairia verbatim na tela: um `timestamptz` não casa com o padrão daquela pura.
 *
 * O `resultado_nome` entra **quando existe**, e a ausência é a informação: concluir sem Resultado
 * é o caso que o FR-17 aceita, e um separador solto no fim diria que faltou alguém preencher.
 *
 * **Ela recebe os dois campos, e não a Tarefa inteira**, e a diferença é o `null`: chamada com a
 * linha em mãos, ela precisaria decidir o que fazer com uma Tarefa pendente — e não há resposta,
 * porque a linha pendente mostra o **prazo** e nunca chega aqui. Com os campos separados, quem
 * chama já passou pela guarda, e o compilador cobra isso.
 */
export function marcaDaConclusao(concluidaEm: string, resultadoNome: string | null): string {
  const quando = `${TAREFA_CONCLUIDA_EM} ${DATA.format(new Date(concluidaEm))}`
  return resultadoNome === null ? quando : `${quando} · ${resultadoNome}`
}

// --- O anexo da Tarefa, conferido **antes** de criar (11.6, AD-9) --------------------------

/**
 * Os tipos de arquivo que o serviço aceita — **espelho** de `MIMES_DOS_ANEXOS`
 * (`crm-backend/servicos/negocios/infra/anexos.py`). O contrato gerado não carrega a lista (ela é
 * política do serviço, não schema), e por isso a cópia é presa ao original por
 * `regras-de-tarefa.test.ts`, que lê o arquivo do backend e compara: divergir reprova ali.
 *
 * A conferência aqui é conveniência — quem recusa de verdade é o serviço (`422`, verbatim). Ela
 * existe para que o arquivo errado seja recusado **antes** de a Tarefa nascer, e não depois, com a
 * Tarefa já criada e o anexo perdido.
 */
export const MIMES_DOS_ANEXOS: readonly string[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/gif',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/zip',
  'text/csv',
  'text/plain',
]

/** O teto por anexo — espelho de `TAMANHO_MAXIMO_DO_ANEXO` (32 MiB), preso pelo mesmo teste. */
export const TAMANHO_MAXIMO_DO_ANEXO = 32 * 1024 * 1024

/** O `accept` do `<input type="file">`: o seletor do sistema já filtra pela lista. */
export const ACEITA_NO_ANEXO = MIMES_DOS_ANEXOS.join(',')

export const ANEXO_DE_TIPO_RECUSADO =
  'Este tipo de arquivo não pode ser anexado. Anexe PDF, imagem, documento, planilha, ' +
  'apresentação, texto ou ZIP.'
export const ANEXO_GRANDE_DEMAIS = `O arquivo passa do limite de ${String(
  TAMANHO_MAXIMO_DO_ANEXO / (1024 * 1024),
)} MB por anexo.`
export const ANEXO_VAZIO = 'O arquivo está vazio.'

/**
 * A recusa local do anexo escolhido — ou `null` quando ele pode subir. O MIME é comparado como o
 * serviço compara: minúsculo e sem parâmetros.
 */
export function recusaDoAnexo(arquivo: File | null): string | null {
  if (arquivo === null) {
    return null
  }
  const mime = (arquivo.type.split(';')[0] ?? '').trim().toLowerCase()
  if (!MIMES_DOS_ANEXOS.includes(mime)) {
    return ANEXO_DE_TIPO_RECUSADO
  }
  if (arquivo.size === 0) {
    return ANEXO_VAZIO
  }
  if (arquivo.size > TAMANHO_MAXIMO_DO_ANEXO) {
    return ANEXO_GRANDE_DEMAIS
  }
  return null
}
